// ============================================================
// Header.jsx
// Top navigation bar — logo, primary nav, theme toggle,
// Messages/Support/Profile/Log Out, and secondary tab row.
// ============================================================

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fonts } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { signOut } from '../utils/auth';
import { getUnreadCrmMessageSummary } from '../utils/crmV2Api';

// ── Navigation config ───────────────────────────────────────
const PRIMARY_NAV = [
  { label: 'Accounts', path: '/dashboard', match: ['/dashboard', '/schwab', '/crypto'] },
  { label: 'Trade', path: '/trade-center', match: ['/trade-center', '/options', '/trades'] },
  { label: 'Research', path: '/comparison', match: ['/comparison', '/calendar', '/risk', '/dividends'] },
  { divider: true },
  { label: 'Fund', path: '/phase3', match: ['/phase3', '/subscribers', '/investors', '/distributions', '/fund-report'] },
  { label: 'CRM', path: '/crm/contacts', match: ['/crm/pipeline', '/crm/relationships', '/crm/firms', '/crm/contacts', '/crm/tasks', '/crm/activity', '/crm/messages', '/crm/templates', '/crm/archive'] },
];

const SECONDARY_NAV = [
  { label: 'Robinhood', path: '/dashboard', section: 'accounts' },
  { label: 'Schwab', path: '/schwab', section: 'accounts' },
  { label: 'Digital Assets', path: '/crypto', section: 'accounts' },
  { label: 'Trading Overview', path: '/trade-center', section: 'trading' },
  { label: 'Comparison', path: '/comparison', section: 'analysis' },
  { label: 'Calendar', path: '/calendar', section: 'analysis' },
  { label: 'Risk', path: '/risk', section: 'analysis' },
  { label: 'Dividends', path: '/dividends', section: 'analysis' },
  { label: 'Overview', path: '/phase3', section: 'fund' },
  { label: 'Members', path: '/subscribers', section: 'fund' },
  { label: 'Distributions', path: '/distributions', section: 'fund' },
  { label: 'Fund Report', path: '/fund-report', section: 'fund' },
  { label: 'Members', path: '/crm/relationships', section: 'crm' },
  { label: 'People', path: '/crm/contacts', section: 'crm' },
  { label: 'Pipeline', path: '/crm/pipeline', section: 'crm' },
  { label: 'Firms', path: '/crm/firms', section: 'crm' },
  { label: 'Tasks', path: '/crm/tasks', section: 'crm' },
  { label: 'Activity', path: '/crm/activity', section: 'crm' },
  { label: 'Messages', path: '/crm/messages', section: 'crm' },
  { label: 'Templates', path: '/crm/templates', section: 'crm' },
  { label: 'Archive', path: '/crm/archive', section: 'crm' },
  { label: 'Account', path: '/profile/account', section: 'settings' },
  { label: 'Linked Accounts', path: '/profile/linked', section: 'settings' },
  { label: 'Data Imports', path: '/profile/crypto', section: 'settings' },
];

const matchesNavPath = (pathname, candidate) => (
  pathname === candidate || pathname.startsWith(`${candidate}/`)
);

const getSection = (pathname) => {
  if (pathname === '/dashboard' || matchesNavPath(pathname, '/schwab') || pathname === '/crypto') return 'accounts';
  if (pathname === '/trade-center' || pathname === '/options' || pathname === '/trades') return 'trading';
  if (pathname === '/comparison' || pathname === '/calendar' || pathname === '/risk' || pathname === '/dividends') return 'analysis';
  if (pathname === '/phase3' || pathname === '/subscribers' || pathname === '/investors' || pathname === '/distributions' || pathname === '/fund-report') return 'fund';
  if (pathname.startsWith('/crm')) return 'crm';
  if (pathname.startsWith('/profile')) return 'settings';
  return 'accounts';
};

// ── Outline-only Sun icon (light mode indicator) ────────────
const SunIcon = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

// ── Outline-only Crescent Moon icon (dark mode indicator) ───
const MoonIcon = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const MessagePulseStyle = () => (
  <style>{`
    @keyframes cnrMessagePulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(88, 166, 255, 0.42); transform: translateY(0); }
      50% { box-shadow: 0 0 0 7px rgba(88, 166, 255, 0); transform: translateY(-1px); }
    }
  `}</style>
);

const Header = ({ isMobile, sticky = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { colors, mode, toggleTheme } = useTheme();
  const [unreadMessages, setUnreadMessages] = useState({ count: 0, latestThreadId: null });

  const section = getSection(location.pathname);
  const primaryActive = PRIMARY_NAV.find((item) => (
    !item.divider && item.match?.some((candidate) => matchesNavPath(location.pathname, candidate))
  ))?.path || '/trade-center';
  const secondaryItems = SECONDARY_NAV.filter((item) => item.section === section);

  // Theme-aware colors
  const headerBg = mode === 'dark' ? 'rgba(22,27,34,0.96)' : 'rgba(255,255,255,0.96)';
  const navText = mode === 'dark' ? '#8b949e' : '#4a6074';
  const navTextDim = mode === 'dark' ? '#6e7681' : '#607487';
  const activeColor = colors.accent;
  const dividerColor = mode === 'dark' ? '#30363d' : '#c5d2db';
  const logoText = mode === 'dark' ? '#f4f7fb' : '#0d3157';
  const logoSubtle = mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(13,49,87,0.08)';

  const headerButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    background: 'transparent',
    border: 'none',
    padding: '4px 8px',
    color: navText,
    fontFamily: fonts.main,
    fontSize: 14,
    fontWeight: 400,
    cursor: 'pointer',
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  useEffect(() => {
    let mounted = true;
    const loadUnread = async () => {
      try {
        const summary = await getUnreadCrmMessageSummary({ manager: true });
        if (mounted) setUnreadMessages(summary);
      } catch {
        if (mounted) setUnreadMessages({ count: 0, latestThreadId: null });
      }
    };
    loadUnread();
    const id = setInterval(loadUnread, 30000);
    window.addEventListener('focus', loadUnread);
    return () => {
      mounted = false;
      clearInterval(id);
      window.removeEventListener('focus', loadUnread);
    };
  }, []);

  return (
    <div
      style={{
        position: sticky ? 'sticky' : 'relative',
        top: 0,
        zIndex: 20,
        background: headerBg,
        borderBottom: `1px solid ${colors.borderSub}`,
        backdropFilter: 'blur(10px)',
      }}
    >
      <MessagePulseStyle />
      <div style={{ maxWidth: 1600, margin: '0 auto', padding: '0 14px' }}>
        {/* ── Primary row: logo + nav + right controls ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 54,
            gap: 16,
          }}
        >
          {/* Left: logo + primary nav */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, minWidth: 0 }}>
            <button
              onClick={() => navigate('/trade-center')}
              style={{
                background: 'transparent',
                border: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: isMobile ? 3 : 5,
                cursor: 'pointer',
                padding: 0,
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  color: logoText,
                  fontFamily: fonts.display,
                  fontSize: isMobile ? 20 : 29,
                  fontWeight: 700,
                  letterSpacing: isMobile ? '0.06em' : '0.09em',
                  lineHeight: 1,
                  textShadow: mode === 'dark' ? '0 1px 0 rgba(255,255,255,0.06)' : 'none',
                  borderBottom: `1px solid ${logoSubtle}`,
                  paddingBottom: 1,
                }}
              >
                CNR Family
              </span>
            </button>

            <div
              style={{
                display: 'flex',
                gap: isMobile ? 14 : 24,
                fontSize: 14,
                color: navText,
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                fontFamily: fonts.main,
              }}
            >
              {PRIMARY_NAV.map((item, idx) => {
                if (item.divider) {
                  return <span key={`div-${idx}`} style={{ color: dividerColor, fontSize: 14, userSelect: 'none' }}>|</span>;
                }
                const active = primaryActive === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      padding: '0 0 2px',
                      color: active ? activeColor : navText,
                      fontSize: 14,
                      fontWeight: active ? 600 : 400,
                      fontFamily: fonts.main,
                      cursor: 'pointer',
                      borderBottom: active ? `2px solid ${activeColor}` : '2px solid transparent',
                    }}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: theme toggle + Profile + Log Out */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexShrink: 0,
            }}
          >
            {/* Theme toggle — sun/moon outline icons */}
            <button
              onClick={toggleTheme}
              title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                padding: '4px 6px',
                cursor: 'pointer',
              }}
            >
              {mode === 'light' ? <MoonIcon color={navTextDim} /> : <SunIcon color={navTextDim} />}
            </button>

            {unreadMessages.count > 0 && (
              <button
                onClick={() => navigate('/crm/messages')}
                style={{
                  ...headerButtonStyle,
                  color: colors.accent,
                  border: `1px solid ${colors.accent}`,
                  borderRadius: 999,
                  padding: '5px 10px',
                  fontWeight: 700,
                  animation: 'cnrMessagePulse 1.35s ease-in-out infinite',
                }}
              >
                You have a new message
              </button>
            )}
            {!isMobile && <button style={headerButtonStyle} onClick={() => navigate('/profile/account')}>Profile</button>}
            <button style={headerButtonStyle} onClick={handleLogout}>Log Out</button>
          </div>
        </div>

        {/* ── Secondary tab row ── */}
        <div
          style={{
            display: 'flex',
            gap: 0,
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            padding: '0',
            borderTop: `1px solid ${colors.borderSub}`,
          }}
        >
          {secondaryItems.map((item) => {
            const active = matchesNavPath(location.pathname, item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                style={{
                  border: 'none',
                  borderBottom: active ? `2px solid ${activeColor}` : '2px solid transparent',
                  background: 'transparent',
                  color: active ? activeColor : navTextDim,
                  padding: '9px 14px 8px',
                  fontSize: 12,
                  fontWeight: active ? 600 : 400,
                  fontFamily: fonts.main,
                  cursor: 'pointer',
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Header;
